import React, { useRef, useState, useEffect } from "react";
import { bool, string, func } from "prop-types";
import { useTranslation } from "@lib/i18n";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";
import getConfig from "next/config";

import ProgressBar from "../progressBar/ProgressBar";
import ContentContainer from "./ContentContainer";
import RedirectBrandIdForms from "components/RedirectBrandIdForms";
import ErrorMessage from "@components/shared/ErrorMessage";
import UserDetailsContainer from "components/UserDetails/Module";

import gtm from "@gtm/core";
import { STEP_INDEXES } from "@shared/constants";

import {
  CTA_TD_TYPES,
  DEAL_TYPE,
  REQUEST_ERROR_CODES,
} from "@shared/constants";
import { generateGitId } from "@gtm/core/utils";
import { useIsUserTrustedSelector } from "@redux/reducers/auth";
import { getUrlWithoutTrailingSlash } from "@shared/v2/lib/getUrlWithoutTrailingSlash";
import { useUserProfileSelector } from "@redux/reducers/deal";
import { useBrandIdSettingsSelector } from "@redux/reducers/appConfig";
import { getCulture } from "@shared/v2/lib/getCulture";

import {
  testDriveDetailsUpdateUrlEvent,
  testDriveDetailsVerificationUpdateUrlEvent,
} from "@gtm/events/testdrive";

import {
  postDealAction,
  patchUserProfileAction,
} from "@redux/actions/dealActions";

const { publicRuntimeConfig } = getConfig();

const { COUNTRY } = publicRuntimeConfig;

const MyDetailsStep = ({
  affiliation,
  handleGitId,
  isFinalStepComplete,
  isStepComplete,
  progressBarColor,
  setIsFinalStepComplete,
  setIsStepComplete,
  returnRoute,
  testDriveType,
  isSSRRendered,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const sectionRef = useRef(null);
  const router = useRouter();

  const isUserTrusted = useIsUserTrustedSelector();
  const userProfile = useUserProfileSelector();
  const { changeEmailUrl } = useBrandIdSettingsSelector();

  const [
    isParkopolyAvailibilityError,
    setIsParkopolyAvailibilityError,
  ] = useState(false);

  useEffect(() => {
    if (isUserTrusted) {
      gtm.fire(
        testDriveDetailsVerificationUpdateUrlEvent({
          type: testDriveType,
          pageTitle: router.asPath,
          affiliation,
          isSSRRendered,
        })
      );
    }
  }, []);

  useEffect(() => {
    if (isStepComplete) {
      window.scrollTo(0, sectionRef.current.offsetTop);
    }
  }, [isFinalStepComplete, isStepComplete]);

  const gtmPersonalDetails = () =>
    gtm.fire(
      testDriveDetailsUpdateUrlEvent({
        type: testDriveType,
        pageTitle: router.asPath,
        affiliation,
        isSSRRendered,
      })
    );

  const onUserDetailsConfirmationSuccess = async nextUserProfile => {
    handleGitId(generateGitId());

    dispatch(patchUserProfileAction({ ...userProfile, ...nextUserProfile }));

    try {
      await dispatch(
        postDealAction(CTA_TD_TYPES.RESERVATION_COMPLETED, DEAL_TYPE.TEST_DRIVE)
      );
    } catch (error) {
      if (!error.isAxiosError) {
        return;
      }

      const { response } = error;

      if (
        response.data.errorCode ===
        REQUEST_ERROR_CODES.SELECTED_PARKOPOLY_TIME_IS_NOT_AVAILABLE
      ) {
        setIsParkopolyAvailibilityError(true);
      }
    }

    setIsFinalStepComplete(true);
    setIsStepComplete(true);
  };

  const onChangeEmailButtonClick = () => {
    const culture = getCulture(language, COUNTRY);

    window.location.href = `${getUrlWithoutTrailingSlash(
      changeEmailUrl
    )}?return_to=${returnRoute}&culture=${culture}`;
  };

  return (
    <section ref={sectionRef}>
      <ProgressBar
        progressBarColor={progressBarColor}
        circleDelay={isStepComplete ? 1.2 : 0.5}
        isComplete={isStepComplete}
        progressLineOnCompleteStep={isFinalStepComplete}
      />
      <ContentContainer
        title={t("testDrive.myDetails")}
        progressBarColor={progressBarColor}
      >
        <AnimatePresence exitBeforeEnter>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.5 }}
          >
            {isUserTrusted ? (
              <>
                <UserDetailsContainer
                  onSubmitSuccessCallback={onUserDetailsConfirmationSuccess}
                  defaultValues={userProfile}
                  returnUrlAfterLogin={returnRoute}
                  isConfirmationButtonVisible={!isFinalStepComplete}
                  onEmailButtonClick={onChangeEmailButtonClick}
                  confirmationButtonLabel={t(
                    "testDrive.myDetailsStep.saveContinue"
                  )}
                  isPersonalInformationEditButtonDisabled={isFinalStepComplete}
                />
                {isParkopolyAvailibilityError && (
                  <ErrorMessage>
                    {t("testDrive.myDetailsStep.timeSlotIsNotAvailable")}
                  </ErrorMessage>
                )}
              </>
            ) : (
              <RedirectBrandIdForms
                redirectLogin={returnRoute}
                redirectRegistration={returnRoute}
                disabled
                isOpen
                gtmEvent={gtmPersonalDetails}
                index={STEP_INDEXES.PERSONAL_DETAILS}
                isHeadTextHidden
                inverted
              />
            )}
          </motion.div>
        </AnimatePresence>
      </ContentContainer>
    </section>
  );
};

MyDetailsStep.propTypes = {
  setIsStepComplete: func.isRequired,
  returnRoute: string.isRequired,
  testDriveType: string.isRequired,
  affiliation: string,
  handleGitId: func,
  isFinalStepComplete: bool,
  isStepComplete: bool,
  progressBarColor: string,
  setIsFinalStepComplete: func,
  isSSRRendered: bool,
};

export default MyDetailsStep;
